import React, { useEffect, useState } from 'react';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import { Box, Divider, IconButton, Typography } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { DialogActionButton, TextField } from '@finpay-development/shared-components';
import { useDispatch } from 'react-redux';
import { Alert } from '../../../meta-data/models/metaData';
import { patchClientAlert, createClientAlert, deleteClientAlert, getAlerts } from '../../../meta-data/state/meta-data-thunk';
import { AppDispatch } from '../../../shared/state/store';

interface EditAlertsModalProps {
    open: boolean;
    onClose: () => void;
    clientId?: number;
    payerAlerts: Alert[];
    employerAlerts: Alert[];
    groupAlerts: Alert[];
    prefixAlerts: Alert[];
    currentPayerName: string | null;
    currentPrefix: string | null;      // display label (full member ID)
    currentGroupCode: string | null;
    currentEmployerName: string | null;
    // alert_ref_id values for creating new alerts when none exist yet
    payerAlertRefId: string | null;
    employerAlertRefId: string | null;
    // actual prefix string and group_code for type 2/3 creates
    prefixValue: string | null;        // matched prefix string e.g. "MTN"
    groupCodeValue: string | null;     // group_code e.g. "GRP-001"
}

// consolidate multiple matched alerts into one (mirrors alert-box.tsx consolidateAlerts)
function consolidate(alerts: Alert[]): Alert | null {
    if (!alerts.length) return null;
    const merged = { ...alerts[0] };
    for (let i = 1; i < alerts.length; i++) {
        merged.note_body = `${merged.note_body}\n\n${alerts[i].note_body}`;
    }
    return merged;
}

interface AlertTypeSection {
    typeId: number;
    label: string;
    currentValue: string | null;  // display label in header
    color: string;
    matchedAlert: Alert | null;
    alertRefId: string | null;    // for types 1/4 creates
    createValue: string | null;   // prefix string (type 2) or group_code (type 3) for creates
}

type AlertEdits = Record<number | string, string>;

function sectionKey(section: AlertTypeSection): number | string {
    return section.matchedAlert ? section.matchedAlert.client_alert_id : `new-${section.typeId}`;
}

function buildEdits(sections: AlertTypeSection[]): AlertEdits {
    const edits: AlertEdits = {};
    for (const section of sections) {
        edits[sectionKey(section)] = section.matchedAlert?.note_body ?? '';
    }
    return edits;
}

interface AlertSectionProps {
    section: AlertTypeSection;
    edits: AlertEdits;
    onChange: (key: number | string, value: string) => void;
    prefixInputValue?: string;
    onPrefixInputChange?: (value: string) => void;
    onDelete?: (section: AlertTypeSection) => void;
}

const AlertSection = ({ section, edits, onChange, prefixInputValue, onPrefixInputChange, onDelete }: AlertSectionProps) => {
    const { label, color, currentValue } = section;
    const key = sectionKey(section);
    const headerText = currentValue ? `${label}: ${currentValue}` : label;
    const isPrefixSection = section.typeId === 2;

    return (
        <Box marginBottom="1.5rem">
            <Box
                style={{
                    backgroundColor: color,
                    padding: '0.75rem 1rem',
                    marginBottom: '0.75rem',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                }}
            >
                <Typography variant="h3">{headerText}</Typography>
                <IconButton
                    size="small"
                    onClick={() => onDelete?.(section)}
                    aria-label={`Delete ${label} alert`}
                >
                    <CloseIcon fontSize="small" />
                </IconButton>
            </Box>
            {isPrefixSection && (
                <Box marginBottom="1rem">
                    <TextField
                        name="prefix-value"
                        label="Prefix"
                        value={prefixInputValue ?? ''}
                        onChange={(e: React.ChangeEvent<HTMLInputElement>) => onPrefixInputChange?.(e.target.value)}
                        onBlur={() => {}}
                    />
                </Box>
            )}
            <TextField
                name={`alert-${key}`}
                label="Note"
                value={edits[key] ?? ''}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => onChange(key, e.target.value)}
                onBlur={() => {}}
                multiline
                rows={3}
            />
        </Box>
    );
};

export const EditAlertsModal = ({
    open,
    onClose,
    clientId,
    payerAlerts,
    employerAlerts,
    groupAlerts,
    prefixAlerts,
    currentPayerName,
    currentPrefix,
    currentGroupCode,
    currentEmployerName,
    payerAlertRefId,
    employerAlertRefId,
    prefixValue,
    groupCodeValue,
}: EditAlertsModalProps) => {
    const dispatch = useDispatch<AppDispatch>();
    const [isSaving, setIsSaving] = useState(false);
    const defaultPrefixInput = (currentPrefix || '').substring(0, 3);
    const [prefixInputValue, setPrefixInputValue] = useState(prefixValue ?? defaultPrefixInput);

    const sections: AlertTypeSection[] = [
        { typeId: 1, label: 'Payer',    color: '#F8E5D8', currentValue: currentPayerName,    matchedAlert: consolidate(payerAlerts),    alertRefId: payerAlertRefId,    createValue: null },
        { typeId: 2, label: 'Prefix',   color: '#E0EAF6', currentValue: currentPrefix,       matchedAlert: consolidate(prefixAlerts),   alertRefId: null,               createValue: prefixInputValue || null },
        { typeId: 3, label: 'Group',    color: '#E4EFDC', currentValue: currentGroupCode,    matchedAlert: consolidate(groupAlerts),    alertRefId: null,               createValue: groupCodeValue },
        { typeId: 4, label: 'Employer', color: '#FDF3D0', currentValue: currentEmployerName, matchedAlert: consolidate(employerAlerts), alertRefId: employerAlertRefId, createValue: null },
    ];

    const [edits, setEdits] = useState<AlertEdits>({});

    useEffect(() => {
        if (open) {
            setEdits(buildEdits(sections));
            const defaultPrefix = (currentPrefix || '').substring(0, 3);
            setPrefixInputValue(prefixValue ?? defaultPrefix);
        }
    }, [open, payerAlerts, employerAlerts, groupAlerts, prefixAlerts, currentPrefix, prefixValue]);

    const handleChange = (key: number | string, value: string) => {
        setEdits(prev => ({ ...prev, [key]: value }));
    };

    const handleDelete = async (section: AlertTypeSection) => {
        if (!clientId) return;
        if (section.matchedAlert) {
            await dispatch(deleteClientAlert({
                clientId,
                clientAlertId: section.matchedAlert.client_alert_id,
            }));
            await dispatch(getAlerts(clientId));
        } else {
            const key = sectionKey(section);
            setEdits(prev => ({ ...prev, [key]: '' }));
            if (section.typeId === 2) {
                setPrefixInputValue((currentPrefix || '').substring(0, 3));
            }
        }
    };

    const handleSave = async () => {
        if (!clientId) return;
        setIsSaving(true);

        await Promise.all(sections.map(section => {
            const note_body = edits[sectionKey(section)] ?? '';
            const isEmpty = !note_body.trim();

            if (section.matchedAlert) {
                if (isEmpty) {
                    return dispatch(deleteClientAlert({
                        clientId,
                        clientAlertId: section.matchedAlert.client_alert_id,
                    }));
                } else {
                    return dispatch(patchClientAlert({
                        clientId,
                        clientAlertId: section.matchedAlert.client_alert_id,
                        note_body,
                    }));
                }
            } else if (!isEmpty) {
                // Type 2 (prefix): send { prefix }
                // Type 3 (group):  send { group_code }
                // Types 1/4:       send { alert_ref_id }
                if (section.typeId === 2 && section.createValue) {
                    return dispatch(createClientAlert({
                        clientId,
                        alert_type_id: 2,
                        note_body,
                        prefix: section.createValue,
                    }));
                } else if (section.typeId === 3 && section.createValue) {
                    return dispatch(createClientAlert({
                        clientId,
                        alert_type_id: 3,
                        note_body,
                        group_code: section.createValue,
                    }));
                } else if (section.alertRefId) {
                    return dispatch(createClientAlert({
                        clientId,
                        alert_type_id: section.typeId,
                        note_body,
                        alert_ref_id: section.alertRefId,
                    }));
                }
            }
            return Promise.resolve();
        }));

        await dispatch(getAlerts(clientId));
        setIsSaving(false);
        onClose();
    };

    return (
        <Dialog
            scroll="body"
            className="modal"
            open={open}
            fullWidth={true}
            maxWidth="lg"
            test-id="edit-alerts-modal"
        >
            <DialogTitle>
                <span className="title">Edit Alerts</span>
            </DialogTitle>
            <DialogContent>
                {sections.map((section, i) => (
                    <React.Fragment key={section.typeId}>
                        <AlertSection
                            section={section}
                            edits={edits}
                            onChange={handleChange}
                            prefixInputValue={section.typeId === 2 ? prefixInputValue : undefined}
                            onPrefixInputChange={section.typeId === 2 ? setPrefixInputValue : undefined}
                            onDelete={handleDelete}
                        />
                        {i < sections.length - 1 && <Divider style={{ marginBottom: '1.5rem' }} />}
                    </React.Fragment>
                ))}
            </DialogContent>
            <DialogActions>
                <DialogActionButton
                    isEnabled={!isSaving}
                    savebuttonText="Save"
                    saveStatus={0}
                    executeSave={handleSave}
                    handleCallbackSave={() => {}}
                    handleCallbackCancel={onClose}
                />
            </DialogActions>
        </Dialog>
    );
};
